import { defineStore } from 'pinia'
import streamerApi from '../api/streamerApi'
import { StreamingError } from '../enums/streamingError'
import { StreamingUseCase } from '../enums/streamingUseCase'
import { StreamingUserRole } from '../enums/streamingUserRole'
import type { Stream, StreamConfig } from '../interfaces/Stream'
import type { StreamingToken } from '../interfaces/StreamingToken'
import { INPUT_CHANNEL_READY, mEvent } from '../utils/events'
import { useUserStore } from './user'

interface State {
    _config: StreamConfig
    _streamingToken: StreamingToken | null
    _useCase: StreamingUseCase | null
    _userRole: StreamingUserRole | null
    _streamingError: StreamingError | null
}

export const useStreamStore = defineStore('stream', {
    state: (): State => ({
        _config: {
            host: '',
            wsPath: '',
            apiPath: '',
            maxBitrate: 0,
        },
        _streamingToken: null,
        _useCase: null,
        _userRole: null,
        _streamingError: null,
    }),

    getters: {
        config: (state): StreamConfig => state._config,

        streamingToken(state): StreamingToken {
            if (!state._streamingToken) {
                throw new Error('Streaming token is not initialized.')
            }

            return state._streamingToken
        },

        streamingError: (state): StreamingError | null => state._streamingError,

        streamUrl(state): string {
            if (!state._streamingToken) {
                throw new Error('Streaming token is not initialized.')
            }

            return `wss://${state._config.host}${state._config.wsPath}?token=${state._streamingToken.token}`
        },

        useCase: (state): StreamingUseCase | null => state._useCase,

        userRole: (state): StreamingUserRole | null => state._userRole,

        maxBitrate: (state): number => state._config.maxBitrate,
    },

    actions: {
        /**
         * Initializes the stream store.
         * Must be called after userStore.init() as it depends on user preferences.
         */
        init(stream: Stream): void {
            this._config = stream.config
            this._streamingError = stream.error
            this._streamingToken = stream.token

            const token = stream.token

            // No token means access was denied (machine_already_in_use, user_kicked_from_group_session, etc.)
            if (!token) {
                return
            }

            this.setUseCase(token.use_case)
            this.setUserRole(token.role)

            // By security, terminate the streaming session as soon as the
            // browser/tab is closed, to make sure streaming token is terminated
            // event if streamer terminate instruction never happens.
            // As sendBeacon is not fully reliable, token terminating from
            // streamer is executed as a fallback.
            window.addEventListener('beforeunload', () => {
                const csrfToken =
                    document
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute('content') || ''

                // Use sendBeacon to ensure the request is sent even when the page is closing
                navigator.sendBeacon(
                    `/api/proxmox-streaming-tokens/${token.token}/terminate`,
                    new Blob([JSON.stringify({ _token: csrfToken })], {
                        type: 'application/json',
                    })
                )
            })

            // Configure stream settings on the streamer service once connection is ready
            mEvent.subscribe(INPUT_CHANNEL_READY, () => {
                const userStore = useUserStore()

                streamerApi.updateBitrate(
                    userStore.user.player_preferences.stream_bitrate_mbps
                )

                streamerApi.updateFpsOptimizationActivation(
                    userStore.user.player_preferences
                        .is_fps_optimization_enabled
                )
            })
        },

        setUseCase(useCase: StreamingUseCase): void {
            this._useCase = useCase
        },

        hasUseCase(useCase: StreamingUseCase): boolean {
            return this._useCase === useCase
        },

        setUserRole(role: StreamingUserRole): void {
            this._userRole = role
        },

        hasUserRole(role: StreamingUserRole): boolean {
            return this._userRole === role
        },
    },
})
